/**
 * Cliente HTTP da API: injeta o access token, e num 401 tenta renovar a sessão
 * uma única vez pelo cookie de refresh antes de desistir.
 *
 * O access token vive só em memória — é o cookie httpOnly (que o JS não lê)
 * que sobrevive ao reload e permite restaurar a sessão.
 */
import type { UsuarioSessao } from './tipos';

let accessToken: string | null = null;
let aoPerderSessao: (() => void) | null = null;

export function definirToken(token: string | null) {
  accessToken = token;
}

export function aoExpirarSessao(callback: () => void) {
  aoPerderSessao = callback;
}

export class ErroApi extends Error {
  constructor(
    public status: number,
    mensagem: string
  ) {
    super(mensagem);
  }
}

async function extrairMensagem(resposta: Response): Promise<string> {
  try {
    const corpo = await resposta.json();
    return corpo.detail ?? corpo.title ?? `Erro ${resposta.status}`;
  } catch {
    return `Erro ${resposta.status}`;
  }
}

async function executar(caminho: string, opcoes: RequestInit = {}): Promise<Response> {
  const cabecalhos = new Headers(opcoes.headers);
  if (accessToken) cabecalhos.set('Authorization', `Bearer ${accessToken}`);
  // FormData é exceção: o navegador precisa gerar o boundary do multipart sozinho.
  // Definir o Content-Type à mão aqui quebraria o envio de arquivo de um jeito
  // difícil de diagnosticar — o servidor recebe um corpo que não sabe separar.
  const ehFormulario = opcoes.body instanceof FormData;
  if (opcoes.body != null && !ehFormulario && !cabecalhos.has('Content-Type')) {
    cabecalhos.set('Content-Type', 'application/json');
  }
  return fetch(caminho, { ...opcoes, headers: cabecalhos });
}

/** Executa com o token e, num 401, tenta renovar a sessão uma única vez. */
async function executarComSessao(caminho: string, opcoes: RequestInit = {}): Promise<Response> {
  let resposta = await executar(caminho, opcoes);

  if (resposta.status === 401 && !caminho.startsWith('/api/auth/')) {
    const sessao = await renovarSessao();
    if (sessao) {
      resposta = await executar(caminho, opcoes);
    } else {
      aoPerderSessao?.();
      throw new ErroApi(401, 'Sessão expirada. Entre de novo.');
    }
  }

  if (!resposta.ok) {
    throw new ErroApi(resposta.status, await extrairMensagem(resposta));
  }
  return resposta;
}

export async function api<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  const resposta = await executarComSessao(caminho, opcoes);
  if (resposta.status === 204) return undefined as T;
  return resposta.json();
}

/**
 * Baixa um binário autenticado (as imagens da wiki são internas — o servidor exige
 * sessão até no GET, e `<img src>` não manda o token; quem baixa é o fetch).
 */
export async function apiBlob(caminho: string): Promise<Blob> {
  const resposta = await executarComSessao(caminho);
  return resposta.blob();
}

export interface RespostaSessao {
  accessToken: string;
  usuario: UsuarioSessao;
}

export async function entrar(email: string, senha: string): Promise<RespostaSessao> {
  const resposta = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, senha }),
  });
  if (!resposta.ok) throw new ErroApi(resposta.status, await extrairMensagem(resposta));
  const sessao: RespostaSessao = await resposta.json();
  definirToken(sessao.accessToken);
  return sessao;
}

/**
 * A renovação que já está no ar, se houver.
 *
 * <p><b>É o que impede a sessão de cair sozinha.</b> O refresh token do servidor é de uso
 * único e gira a cada uso: quem o apresenta recebe outro, e o apresentado é revogado na
 * hora. Isso é bom — token vazado só vale uma vez — e é exatamente o que torna duas
 * renovações simultâneas fatais.
 *
 * <p>E elas eram a regra, não a exceção. Toda tela dispara várias consultas de uma vez;
 * quando o access token vence, <i>todas</i> voltam 401 no mesmo instante e cada uma
 * chamava `/api/auth/refresh` por conta própria. A primeira girava o token, e as outras
 * chegavam com um valor já revogado — 401 de novo, `aoPerderSessao()`, tela de login. O
 * usuário via "o sistema me desconectou" a cada meia hora de trabalho, que é a validade
 * do access token.
 *
 * <p>Com a promessa compartilhada, as N chamadas esperam a <b>mesma</b> renovação e
 * recebem o mesmo token novo. O servidor vê uma rotação, que é o que de fato aconteceu.
 */
let renovacaoEmVoo: Promise<RespostaSessao | null> | null = null;

/**
 * Tenta restaurar a sessão pelo cookie httpOnly. Nulo quando não há sessão viva.
 *
 * <p>Chamadas concorrentes compartilham a mesma ida ao servidor — ver `renovacaoEmVoo`.
 */
export function renovarSessao(): Promise<RespostaSessao | null> {
  if (renovacaoEmVoo) return renovacaoEmVoo;

  renovacaoEmVoo = (async () => {
    try {
      const resposta = await fetch('/api/auth/refresh', { method: 'POST' });
      if (!resposta.ok) return null;
      const sessao: RespostaSessao = await resposta.json();
      definirToken(sessao.accessToken);
      return sessao;
    } finally {
      // Some antes de quem espera acordar — quem já segura a promessa continua com ela,
      // e a próxima renovação (outra expiração, daqui a meia hora) começa do zero.
      renovacaoEmVoo = null;
    }
  })();

  return renovacaoEmVoo;
}

export async function sair(): Promise<void> {
  // Descartar a renovação em voo antes de sair: sem isto, uma que estivesse no ar
  // devolveria um token novo depois do logout e a sessão ressuscitaria em memória.
  renovacaoEmVoo = null;
  await fetch('/api/auth/logout', { method: 'POST' });
  definirToken(null);
}
